import {
  authApi,
  type AuthUser,
  type AuthUserResult,
  type AzureAdRole,
  type Impersonator,
  type TestProfile,
} from "@/api/auth";
import { defineStore } from "pinia";

export type AuthState = {
  isAuthenticated: boolean;
  isAdmin: boolean;
  roles: string[];
  isAzureAdUser: boolean;
  azureAdRoles: AzureAdRole[];
  isImpersonating: boolean;
  impersonator: Impersonator | null;
  testProfile: TestProfile | null;
  route: string;
  user: AuthUser | null;
};

export const useAuthStore = defineStore("auth", {
  state: (): AuthState => ({
    isAuthenticated: false,
    isAdmin: false,
    roles: [],
    isAzureAdUser: false,
    azureAdRoles: [],
    isImpersonating: false,
    impersonator: null,
    testProfile: null,
    route: "/",
    user: null,
  }),
  getters: {
    hasRole: (state) => (role: string) =>
      state.isAdmin || state.roles.includes(role),
    canAccessAdmin: (state) =>
      state.isAdmin ||
      state.roles.includes("Impersonators") ||
      state.roles.includes("OperationsManagers"),
    adminLandingRoute: (state): string => {
      if (state.isAdmin) return "/admin/users";
      if (state.roles.includes("OperationsManagers")) return "/admin/bonus";
      return "/reports";
    },
  },
  actions: {
    applyAuth(auth: AuthUserResult) {
      this.$patch({
        user: auth,
        isAuthenticated: true,
        isAdmin: auth.isAdmin ?? false,
        roles: auth.roles ?? [],
        isAzureAdUser: auth.isAzureAdUser ?? false,
        azureAdRoles: auth.azureAdRoles ?? [],
        isImpersonating: auth.isImpersonating ?? false,
        impersonator: auth.impersonator ?? null,
        testProfile: auth.testProfile ?? null,
      });
    },
    async authenticated() {
      const auth = await authApi.authenticated();
      if (auth !== null) {
        this.applyAuth(auth);
      }

      return auth !== null;
    },
    async login(userName: string, password: string, rememberMe: boolean) {
      const auth = await authApi.login(userName, password, rememberMe);
      if (auth) {
        this.applyAuth(auth);
        return true;
      } else {
        return false;
      }
    },
    loginWithAzureAd() {
      authApi.loginWithAzureAd();
    },
    async impersonate(userId: string) {
      const auth = await authApi.impersonate(userId);
      this.applyAuth(auth);
    },
    async setTestProfile(profile: {
      jobTitle: string | null;
      locationCode: string | null;
    }) {
      const auth = await authApi.setTestProfile(profile);
      this.applyAuth(auth);
    },
    async stopImpersonation() {
      const auth = await authApi.stopImpersonation();
      this.applyAuth(auth);
    },
    async logout() {
      await authApi.logout();

      this.$state = {
        isAuthenticated: false,
        isAdmin: false,
        roles: [],
        isAzureAdUser: false,
        azureAdRoles: [],
        isImpersonating: false,
        impersonator: null,
        testProfile: null,
        route: "/",
        user: null,
      };
    },
  },
});
